import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { Sale } from './sale';
export { Sale };
import { saleSchema } from '@schemas/business-objects/sale';
import { SalePhotoFormArray } from './sale-photo-form-group';
import type { MeasurementType } from './measurement-type';
import type { SystemTaskExecution } from './system-task-execution';

export class SaleFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    startDate: SchemaFormControl;
    endDate: SchemaFormControl;
    salePhotos: SalePhotoFormArray;
  };

  declare value: Partial<Sale>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(saleSchema, Sale, data, options);
  }

  get object(): Sale {
    return new Sale(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'salePhotos':
        return new SalePhotoFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<Sale[]> {
    return Sale.find(filter);
  }

  static async findOne(filter?: any): Promise<Sale | null> {
    return Sale.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<Sale> {
    return Sale.findById(id, filter);
  }

  static async create(data: Partial<Sale>): Promise<Sale> {
    return Sale.create(data as any);
  }

  static async updateById(id: number, updates: Partial<Sale>): Promise<Sale> {
    return Sale.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return Sale.deleteById(id);
  }

  async readBanners(): Promise<number> {
    const instance = new Sale(this.getRawValue());
    return instance.readBanners();
  }

  async exportMeasurementsCsv(): Promise<any> {
    const instance = new Sale(this.getRawValue());
    return instance.exportMeasurementsCsv();
  }
}

export class SaleFormArray extends PersistedFormArray {
  readonly entityName = 'Sale' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(saleSchema, Sale, options);
  }

  protected override createItemGroup() {
    return new SaleFormGroup();
  }

  get array(): Sale[] {
    return this.controls.map((group: SaleFormGroup) => group.object);
  }
}

export class SalePersistedArray extends PersistedArray<Sale> {
  readonly entityName = 'Sale' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Sale, options);
  }
}
